import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';
import { GroupChartTabs, GroupedTabData, ChartDataPoint } from './group-chart.modals';
import { ProcessingState } from '@services/core-services/models';
import { PaginatedDataService, PaginationInfo } from './paginated-data.service';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { ChartMetricsService } from './chart-metrics.service';
import { MetricSetDefinition, DimensionDefinition } from './metric-set.models';

export interface EnhancedChartDataPoint extends ChartDataPoint {
  metricSet?: string;
  metrics?: { title: string; value: any }[];
}

export interface GroupedDataState {
  data: BehaviorSubject<any[]>;
  stats: BehaviorSubject<GroupedTabData>;
  allData: BehaviorSubject<GroupedTabData>;
  tabs: BehaviorSubject<GroupChartTabs[]>;
  defaultTab: BehaviorSubject<string>;
  pagination: BehaviorSubject<PaginationInfo>;
  loader: BehaviorSubject<ProcessingState>;
}

@Injectable({
  providedIn: 'root',
})
export class GroupedDataStoreService {
  constructor(
    private readonly paginatedDataService: PaginatedDataService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly chartMetricsService: ChartMetricsService
  ) {}

  createGroupedDataState(defaultTabId: string = ''): GroupedDataState {
    const paginatedState = this.paginatedDataService.createPaginatedState();

    return {
      data: new BehaviorSubject<any[]>([]),
      stats: paginatedState.data,
      allData: paginatedState.allData,
      tabs: new BehaviorSubject<GroupChartTabs[]>([]),
      defaultTab: new BehaviorSubject<string>(defaultTabId),
      pagination: paginatedState.pagination,
      loader: paginatedState.loader,
    };
  }

  /**
   * Updates pagination for a specific tab within a grouped data state
   */
  updatePagination(state: GroupedDataState, tabId: string, page: number, limit: number = 8): void {
    const allData = state.allData.getValue();
    const currentStats = state.stats.getValue();

    const { paginationInfo, updatedStats } = this.paginatedDataService.updatePagination(
      allData,
      tabId,
      currentStats,
      page,
      limit
    );

    state.pagination.next(paginationInfo);
    state.stats.next(updatedStats);
  }

  /**
   * Processes raw data into a grouped data structure suitable for charts and tables
   *
   * @param state The state object to update
   * @param list The raw data list to process
   * @param fieldConfig Configuration specifying which fields to extract and how to group them
   * @param labelExtractor A function that extracts a label from a data item
   * @param valueExtractor A function that extracts a value from a data item for a specific field
   */
  processGroupedData(
    state: GroupedDataState,
    list: any[],
    fieldConfig: { id: string; label: string }[],
    labelExtractor: (item: any) => string,
    valueExtractor: (item: any, field: string) => number
  ): void {
    state.loader.next(ProcessingState.Loading);

    try {
      if (!Array.isArray(list) || list.length === 0) {
        this.resetState(state);
        state.loader.next(ProcessingState.Success);
        return;
      }

      const fieldsPresence: { [key: string]: boolean } = {};
      fieldConfig.forEach(field => {
        fieldsPresence[field.id] = false;
      });

      list.forEach(item => {
        fieldConfig.forEach(field => {
          if (valueExtractor(item, field.id)) {
            fieldsPresence[field.id] = true;
          }
        });
      });

      const tabs = fieldConfig
        .filter(field => fieldsPresence[field.id])
        .map(field => ({ id: field.id, label: field.label }));

      const defaultTab = tabs.length > 0 ? tabs[0].id : '';

      const allData: GroupedTabData = {};
      tabs.forEach(tab => {
        allData[tab.id] = [];
      });

      list.forEach(item => {
        fieldConfig.forEach(field => {
          const fieldId = field.id;

          if (valueExtractor(item, fieldId) && fieldsPresence[fieldId]) {
            const label = labelExtractor(item);
            const value = valueExtractor(item, fieldId);

            const dataPoint: ChartDataPoint = {
              label,
              value,
            };

            if (!allData[fieldId].some(existing => existing.label === label)) {
              allData[fieldId].push(dataPoint);
            } else {
              const existingItem = allData[fieldId].find(existing => existing.label === label);
              if (existingItem) {
                existingItem.value += value;
              }
            }
          }
        });
      });

      for (const key in allData) {
        allData[key].sort((a, b) => b.value - a.value);
      }

      state.data.next(list);
      state.allData.next(allData);
      state.tabs.next(tabs);
      state.defaultTab.next(defaultTab);

      tabs.forEach(tab => {
        this.updatePagination(state, tab.id, 1);
      });

      state.loader.next(ProcessingState.Success);
    } catch (error) {
      console.error('Error processing grouped data:', error);
      this.resetState(state);
      state.loader.next(ProcessingState.Error);
    }
  }

  /**
   * Process state or courier grouped data with enhanced metric handling
   *
   * @param state The state object to update
   * @param list The raw data list to process
   * @param groupByField The field name to group by (e.g., 'state', 'courier')
   * @param metricSetName The name of the metric set to use (from ChartMetricsService)
   */
  processEntityGroupedData(
    state: GroupedDataState,
    list: any[],
    groupByField: string = 'state',
    metricSetName?: string
  ): void {
    try {
      state.loader.next(ProcessingState.Loading);
      state.data.next(list);

      if (!Array.isArray(list) || list.length === 0) {
        this.resetGroupedState(state);
        state.loader.next(ProcessingState.Success);
        return;
      }

      // Always rely on ChartMetricsService to identify and provide the definition
      const actualMetricSetName = metricSetName || this.detectMetricSetFromData(list[0]);
      const metricSetDefinition =
        this.chartMetricsService.getMetricSetDefinition(actualMetricSetName);

      // If no definition found, we cannot process reliably
      if (!metricSetDefinition) {
        this.resetGroupedState(state);
        state.loader.next(ProcessingState.Error); // Indicate error due to missing config
        return;
      }

      // --- Unified Processing Logic ---
      const availableDimensions = this.detectAvailableDimensions(list, metricSetDefinition);
      const tabs = this.createTabsFromDimensions(availableDimensions);
      const defaultTab = this.determineDefaultTab(
        availableDimensions,
        // Use explicit groupByField as the preferred default, then definition default
        groupByField || metricSetDefinition.defaultDimension
      );

      // Use the new unified aggregation function
      const tabData = this.aggregateData(list, availableDimensions, metricSetDefinition);

      // Sort and filter data points
      Object.keys(tabData).forEach(dimension => {
        tabData[dimension] = tabData[dimension].filter(
          point => point.label !== 'Unknown' && point.label !== ''
        );
        // Sorting will happen within convertToChartDataPoints based on primary value
        // .sort((a, b) => b.value - a.value); // Remove sorting here
      });

      state.allData.next(tabData);
      // stats will be updated via pagination
      // state.stats.next(tabData);
      state.tabs.next(tabs);
      state.defaultTab.next(defaultTab);

      // Initialize pagination for each tab
      tabs.forEach(tab => {
        this.updatePagination(state, tab.id, 1);
      });
      // --- End Unified Processing ---

      state.loader.next(ProcessingState.Success);
    } catch (error) {
      console.error(`Error processing entity grouped data:`, error);
      this.resetGroupedState(state);
      state.loader.next(ProcessingState.Error);
    }
  }

  /**
   * Detect which dimensions are available in the data
   */
  private detectAvailableDimensions(
    list: any[],
    metricSetDefinition: MetricSetDefinition
  ): DimensionDefinition[] {
    const foundDimensions: { [key: string]: boolean } = {};

    metricSetDefinition.dimensions.forEach(dimension => {
      const hasDimension = list.some(item => item[dimension.key] !== undefined);
      foundDimensions[dimension.key] = hasDimension;
    });

    return metricSetDefinition.dimensions.filter(dimension => foundDimensions[dimension.key]);
  }

  /**
   * Create tab definitions from available dimensions
   */
  private createTabsFromDimensions(dimensions: DimensionDefinition[]): GroupChartTabs[] {
    const sortedDimensions = [...dimensions].sort(
      (a, b) => (a.priority || 100) - (b.priority || 100)
    );

    return sortedDimensions.map(dimension => ({
      id: dimension.key,
      label: dimension.label,
    }));
  }

  /**
   * Determine which dimension should be the default tab
   */
  private determineDefaultTab(dimensions: DimensionDefinition[], explicitDefault?: string): string {
    if (explicitDefault && dimensions.some(d => d.key === explicitDefault)) {
      return explicitDefault;
    }

    const defaultDimension = dimensions.find(d => d.isDefault);
    if (defaultDimension) {
      return defaultDimension.key;
    }

    return dimensions.length > 0 ? dimensions[0].key : '';
  }

  /**
   * New unified aggregation function based on MetricSetDefinition
   */
  private aggregateData(
    list: any[],
    dimensions: DimensionDefinition[],
    metricSetDefinition: MetricSetDefinition
  ): GroupedTabData {
    const groupedAggregates: { [dimensionKey: string]: { [dimensionValue: string]: any } } = {};

    // Initialize structure for each dimension
    dimensions.forEach(dim => {
      groupedAggregates[dim.key] = {};
    });

    // Process each item from the raw list
    list.forEach(item => {
      dimensions.forEach(dim => {
        const dimensionKey = dim.key;
        const dimensionValue = item[dimensionKey];

        // Skip if item doesn't have this dimension or value is invalid
        if (dimensionValue === undefined || dimensionValue === null || dimensionValue === '') {
          return;
        }

        const aggregateGroup = groupedAggregates[dimensionKey];
        let aggregate = aggregateGroup[dimensionValue];

        // Initialize aggregate for this dimension value if first time seen
        if (!aggregate) {
          aggregate = { [dimensionKey]: dimensionValue }; // Add dimension itself
          metricSetDefinition.metrics.forEach(metric => {
            aggregate[metric.key] = 0; // Initialize metric value
            // Initialize accumulators needed for specific aggregation methods
            // No longer need type assertions
            if (metric.aggregationMethod === 'weightedAverage') {
              aggregate[`${metric.key}_weighted_sum`] = 0;
              aggregate[`${metric.key}_total_weight`] = 0;
            } else if (metric.aggregationMethod === 'average') {
              aggregate[`${metric.key}_sum`] = 0;
              aggregate[`${metric.key}_count`] = 0;
            }
            // 'sum' doesn't need extra accumulators
          });
          aggregateGroup[dimensionValue] = aggregate;
        }

        // Perform aggregation for each metric based on its definition
        metricSetDefinition.metrics.forEach(metric => {
          const metricKey = metric.key;
          const metricValue = Number(item[metricKey]);

          if (!isNaN(metricValue)) {
            // Only aggregate valid numbers
            // No longer need type assertion for aggregationMethod
            const aggMethod = metric.aggregationMethod || 'sum'; // Default to sum

            switch (aggMethod) {
              case 'weightedAverage': {
                // Create block scope for lexical declaration
                // No longer need type assertion for weightMetricKey
                const weightMetricKey = metric.weightMetricKey;
                if (weightMetricKey) {
                  const weightValue = Number(item[weightMetricKey]);
                  if (!isNaN(weightValue) && weightValue > 0) {
                    const currentSum = aggregate[`${metricKey}_weighted_sum`] || 0;
                    const currentWeight = aggregate[`${metricKey}_total_weight`] || 0;
                    aggregate[`${metricKey}_weighted_sum`] = currentSum + metricValue * weightValue;
                    aggregate[`${metricKey}_total_weight`] = currentWeight + weightValue;
                  }
                } else {
                  console.warn(
                    `[aggregateData] Weighted average requested for metric '${metricKey}' but no weightMetricKey provided.`
                  );
                }
                break;
              }
              case 'average':
                aggregate[`${metricKey}_sum`] += metricValue;
                aggregate[`${metricKey}_count`] += 1;
                break;
              case 'sum':
              default: // Default to sum if not specified or unknown
                aggregate[metricKey] += metricValue;
                break;
            }
          }
        });
      });
    });

    // Final calculation pass (e.g., compute averages)
    const finalGroupedData: GroupedTabData = {};
    dimensions.forEach(dim => {
      const dimensionKey = dim.key;
      const aggregatesForDim = groupedAggregates[dimensionKey];
      const finalDataPoints: EnhancedChartDataPoint[] = [];

      Object.values(aggregatesForDim).forEach(aggregate => {
        metricSetDefinition.metrics.forEach(metric => {
          const metricKey = metric.key;
          const aggMethod = metric.aggregationMethod || 'sum';
          // No longer need type assertion
          if (aggMethod === 'weightedAverage') {
            const weightedSum = aggregate[`${metricKey}_weighted_sum`];
            const totalWeight = aggregate[`${metricKey}_total_weight`];
            const avg = totalWeight > 0 ? weightedSum / totalWeight : 0;
            aggregate[metricKey] = avg; // Store final average
            delete aggregate[`${metricKey}_weighted_sum`];
            delete aggregate[`${metricKey}_total_weight`];
            // No longer need type assertion
          } else if (aggMethod === 'average') {
            const sum = aggregate[`${metricKey}_sum`];
            const count = aggregate[`${metricKey}_count`];
            aggregate[metricKey] = count > 0 ? sum / count : 0;
            delete aggregate[`${metricKey}_sum`];
            delete aggregate[`${metricKey}_count`];
          }
        });

        // Convert the final aggregate to a ChartDataPoint
        finalDataPoints.push(
          this.convertToChartDataPoint(aggregate, dimensionKey, metricSetDefinition)
        );
      });

      // Sort based on primary metric defined in the set, or fallback
      // No longer need type assertion for primaryMetricKey
      const primaryMetricKey =
        metricSetDefinition.primaryMetricKey || metricSetDefinition.metrics[0]?.key || 'value';
      finalDataPoints.sort((a, b) => {
        // Use bracket notation for indexed access to originalData
        // Ensure type safety with explicit checks or casting if necessary
        const valA = a['originalData']?.[primaryMetricKey] ?? a.value ?? 0;
        const valB = b['originalData']?.[primaryMetricKey] ?? b.value ?? 0;
        return valB - valA; // Descending sort
      });

      finalGroupedData[dimensionKey] = finalDataPoints;
    });

    return finalGroupedData;
  }

  /**
   * Convert aggregated data to a single chart data point
   */
  private convertToChartDataPoint(
    // Rename: aggregatedData is now a single aggregate object
    aggregate: { [key: string]: any },
    dimensionKey: string,
    metricSetDefinition: MetricSetDefinition
  ): EnhancedChartDataPoint {
    // const chartPoints: EnhancedChartDataPoint[] = []; // Removed array init

    // Get the primary value metric key
    // No longer need type assertion
    const primaryMetricKey =
      metricSetDefinition.primaryMetricKey || metricSetDefinition.metrics[0]?.key || 'value';

    // Determine the primary value from the aggregate
    const primaryValue =
      typeof aggregate[primaryMetricKey] === 'number'
        ? aggregate[primaryMetricKey]
        : (aggregate['value'] ?? aggregate['orders_shipped'] ?? 0); // Sensible fallbacks

    // Extract metrics using ChartMetricsService
    const metrics = this.chartMetricsService.extractMetrics(aggregate, metricSetDefinition.name);

    const dataPoint: EnhancedChartDataPoint = {
      label: aggregate[dimensionKey] || 'Unknown',
      value: primaryValue, // Use the determined primary value
      originalData: aggregate, // Keep the full aggregated data
      metricSet: metricSetDefinition.name,
      metrics: metrics, // Include extracted/formatted metrics
    };

    // chartPoints.push(dataPoint); // Removed push to array

    return dataPoint; // Return the single data point
  }

  /**
   * Detects the appropriate metric set based on the data structure
   * @param data The data object to analyze
   * @returns The name of the detected metric set
   */
  private detectMetricSetFromData(data: any): string {
    // Use the ChartMetricsService to identify the appropriate metric set
    return this.chartMetricsService.identifyMetricSet(data);
  }

  /**
   * Helper method to capitalize the first letter of a string
   */
  private capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Loads data from an API and processes it into grouped data
   *
   * @param state The state object to update
   * @param dataLoader A function that loads data from an API
   * @param fieldConfig Configuration specifying which fields to extract and how to group them
   * @param labelExtractor A function that extracts a label from a data item
   * @param valueExtractor A function that extracts a value from a data item for a specific field
   * @param responseExtractor A function that extracts the data list from the API response
   */
  loadAndProcessData<T>(
    state: GroupedDataState,
    dataLoader: Observable<T>,
    fieldConfig: { id: string; label: string }[],
    labelExtractor: (item: any) => string,
    valueExtractor: (item: any, field: string) => number,
    responseExtractor: (response: T) => any[] = response => this.extractResponseList(response)
  ): Observable<T> {
    state.loader.next(ProcessingState.Loading);

    return dataLoader.pipe(
      tap(response => {
        const list = responseExtractor(response);
        this.processGroupedData(state, list, fieldConfig, labelExtractor, valueExtractor);
      }),
      catchError(error => {
        console.error('Error loading data:', error);
        this.resetState(state);
        state.loader.next(ProcessingState.Error);
        return of([] as unknown as T);
      })
    );
  }

  /**
   * Load and process entity-grouped data (state or courier)
   */
  loadAndProcessEntityData<T>(
    state: GroupedDataState,
    dataLoader: Observable<T>,
    groupByField: string = 'state',
    metricSetName?: string,
    responseExtractor: (response: T) => any[] = response => this.extractResponseList(response)
  ): Observable<T> {
    state.loader.next(ProcessingState.Loading);

    return dataLoader.pipe(
      tap(response => {
        const list = responseExtractor(response);
        this.processEntityGroupedData(state, list, groupByField, metricSetName);
      }),
      catchError(error => {
        console.error(`Error loading ${groupByField} data:`, error);
        this.resetGroupedState(state);
        state.loader.next(ProcessingState.Error);
        return of([] as unknown as T);
      })
    );
  }

  /**
   * Helper method to extract a list from various response formats
   */
  private extractResponseList(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      if (response.list && Array.isArray(response.list)) {
        return response.list;
      } else if (response.stats && Array.isArray(response.stats)) {
        return response.stats;
      }
    }
    return [];
  }

  /**
   * Resets a state to empty values
   */
  private resetState(state: GroupedDataState): void {
    this.resetGroupedState(state);
  }

  /**
   * Resets a grouped state to empty values
   */
  private resetGroupedState(state: GroupedDataState): void {
    state.data.next([]);
    state.stats.next({});
    state.allData.next({});
    state.tabs.next([]);
    state.defaultTab.next('');
    state.pagination.next({
      limit: 8,
      current_page: 1,
      total_records: 0,
      total_pages: 1,
      next_page: null,
      previous_page: null,
    });
  }
}
